import { Box, Typography } from '@mui/material';
import { Users } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import type { AdminStatistics } from '@/admin/adminApi';
import { LineChart, StackedBarChart } from '@/ui/charts';
import { Card, EmptyState, Metric } from '@/ui/kit';

import {
  countLabel,
  describeWeeklySeries,
  describeWeeks,
  entriesSeries,
  hasReadableValue,
  newAccountsValues,
  readCount,
  suppressedLabel,
  weekLabel,
} from './statisticsModel';

/**
 * Corps de l'ecran des statistiques d'usage, une fois la reponse lue.
 *
 * Quatre choses y sont **dites** plutot que devinees :
 *
 * - **un nombre supprime s'ecrit « < k »**, avec le `k` que le serveur rend, dans
 *   les tuiles comme dans les tables des graphiques — jamais un nombre, jamais `0`,
 *   jamais un tiret (un tiret dirait « donnee manquante », pas « groupe trop
 *   petit ») ;
 * - **il n'y a pas de courbe des comptes actifs** : le serveur ne garde que le
 *   dernier renouvellement de chaque session, un releve passe ne compterait que
 *   ceux qui ont cesse de venir. Seules les valeurs courantes sont servies ;
 * - **les saisies par nature ne sont pas empilees** : un empilement dit un total,
 *   que la suppression d'un segment rendrait faux. Une courbe par nature, qui
 *   s'interrompt sous le seuil ;
 * - **la semaine en cours est marquee** « (en cours) » : ses jours a venir ne sont
 *   pas ecoules, son creux n'est pas une baisse d'usage.
 *
 * **Aucun total, aucun pourcentage, aucun ecart n'est calcule ici.**
 */

const Definition = ({ term, children }: { readonly term: string; readonly children: ReactNode }): ReactElement => (
  <Box>
    <Typography component="dt" sx={{ fontSize: 14, fontWeight: 700 }}>
      {term}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 14, lineHeight: '20px', color: 'tokens.textMuted' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Definitions ecrites a l'ecran : ce que « actif » veut dire, ce que le seuil
 * promet — **et ne promet pas** : c'est un plancher d'affichage, le role
 * `support` lit deja, de facon auditee, les donnees d'une famille (#525).
 */
export const StatisticsDefinitions = ({ k, timezone }: { readonly k: number; readonly timezone: string }): ReactElement => {
  const floor = suppressedLabel(k);

  return (
    <Card title="Comment lire ces chiffres">
      <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1.5 }}>
        <Definition term="Compte actif">
          Au moins une session non révoquée renouvelée dans la fenêtre (24 heures, 7 jours ou 30 jours glissants), hors
          comptes d’administration et comptes anonymisés. C’est une mesure en ligne : un usage purement hors ligne n’est pas
          vu avant la prochaine synchronisation. Il n’y a pas de courbe des comptes actifs : le serveur ne garde que le
          dernier renouvellement de chaque session, un relevé passé ne serait pas exact.
        </Definition>
        <Definition term={`Seuil d’affichage : ${floor}`}>
          Tout compteur issu de moins de {k.toLocaleString('fr-FR')} comptes s’affiche « {floor} » : le serveur ne rend alors
          aucun nombre. Le seuil porte sur le nombre de comptes, pas sur la valeur : un total de saisies n’est affiché que si
          au moins {k.toLocaleString('fr-FR')} comptes y ont contribué. C’est un plancher d’affichage, pas une protection : le
          rôle support lit déjà, de façon auditée, les données d’une famille.
        </Definition>
        <Definition term="Semaines">
          Semaines calendaires, du lundi au dimanche ({timezone}). La dernière est en cours : ses jours à venir ne sont pas
          écoulés, son creux n’est pas une baisse d’usage.
        </Definition>
        <Definition term="Saisies par nature">
          Créations de saisie reçues par le serveur, à la date de réception et non à celle de la saisie. Un compte « a
          enregistré au moins une » saisie : on ne sait pas s’il l’a consultée.
        </Definition>
      </Box>
    </Card>
  );
};

const ActiveTiles = ({ statistics }: { readonly statistics: AdminStatistics }): ReactElement => {
  const { active, k } = statistics;
  const tiles = [
    { id: 'day', label: 'Comptes actifs · 24 h', window: 'sur 24 heures glissantes', count: active.day },
    { id: 'week', label: 'Comptes actifs · 7 jours', window: 'sur 7 jours glissants', count: active.week },
    { id: 'month', label: 'Comptes actifs · 30 jours', window: 'sur 30 jours glissants', count: active.month },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      {tiles.map((tile) => (
        <Metric
          key={tile.id}
          label={tile.label}
          icon={Users}
          value={countLabel(tile.count, k)}
          caption={readCount(tile.count) === null ? `Moins de ${k.toLocaleString('fr-FR')} comptes : nombre non affiché` : tile.window}
        />
      ))}
    </Box>
  );
};

const InProgressNote = (): ReactElement => (
  <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>
    La dernière semaine, marquée « en cours », n’est pas écoulée : son creux n’est pas une baisse d’usage.
  </Typography>
);

/** Dit ce que veut dire l'absence de marque sous un graphique : sous le seuil, jamais zéro. */
const BelowFloorNote = ({ mark, floor, children }: { readonly mark: string; readonly floor: string; readonly children?: string }): ReactElement => (
  <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>
    {`Une semaine sans ${mark} est sous le seuil (« ${floor} ») : le serveur ne rend pas le nombre, ce n’est pas zéro.`}
    {children ? ` ${children}` : ''}
  </Typography>
);

const WeeklyCharts = ({ statistics }: { readonly statistics: AdminStatistics }): ReactElement => {
  const { weekly, k } = statistics;
  const labels = weekly.map(weekLabel);
  const floor = suppressedLabel(k);
  const accounts = newAccountsValues(weekly);
  const kinds = entriesSeries(weekly);
  const noneAboveFloor = `Aucune semaine n’atteint le seuil : toutes les valeurs sont « ${floor} ».`;

  return (
    <>
      <Card title="Nouveaux comptes par semaine" subtitle="Comptes créés dans la semaine, hors comptes d’administration">
        <StackedBarChart
          label="Nouveaux comptes par semaine"
          categories={labels}
          categoryHeader="Semaine du lundi"
          series={[{ label: 'Nouveaux comptes', values: accounts }]}
          missingLabel={floor}
          emptyMessage={noneAboveFloor}
          summary={`${describeWeeks(weekly)} ${describeWeeklySeries('Nouveaux comptes', weekly, accounts, k)}`}
        />
        <BelowFloorNote mark="barre" floor={floor} />
        <InProgressNote />
      </Card>
      <Card title="Saisies par nature, par semaine" subtitle="Créations de saisie reçues par le serveur ; une courbe s’interrompt sous le seuil">
        <LineChart
          label="Saisies par nature, par semaine"
          categories={labels}
          categoryHeader="Semaine du lundi"
          series={kinds}
          missingLabel={floor}
          emptyMessage={noneAboveFloor}
          summary={`${describeWeeks(weekly)} ${kinds.map((one) => describeWeeklySeries(one.label, weekly, one.values, k)).join(' ')}`}
        />
        <BelowFloorNote mark="point" floor={floor}>
          Les natures ne sont pas additionnées, un total ne serait pas fiable quand l’une d’elles est masquée.
        </BelowFloorNote>
        <InProgressNote />
      </Card>
    </>
  );
};

export const StatisticsContent = ({ statistics }: { readonly statistics: AdminStatistics }): ReactElement => {
  if (!hasReadableValue(statistics)) {
    return (
      <>
        <EmptyState
          title={`Pas encore assez de comptes pour afficher des statistiques (moins de ${statistics.k.toLocaleString('fr-FR')})`}
          description={`Aucun compteur n’atteint le seuil d’affichage : le serveur ne rend aucun nombre issu de moins de ${statistics.k.toLocaleString('fr-FR')} comptes. Les chiffres paraîtront dès qu’un compteur aura ce nombre de comptes derrière lui.`}
          icon={Users}
        />
        <StatisticsDefinitions k={statistics.k} timezone={statistics.timezone} />
      </>
    );
  }

  return (
    <>
      <ActiveTiles statistics={statistics} />
      <WeeklyCharts statistics={statistics} />
      <StatisticsDefinitions k={statistics.k} timezone={statistics.timezone} />
    </>
  );
};
